/**
 * Vitals editor (V1 — S-25, PRD #7): height, job, education, drinking,
 * smoking, kids, religion, politics, zodiac — all optional, saved via
 * PATCH /me/profile {vitals}. Enum-ish fields get selects, the rest text.
 */
import { useEffect, useState } from "react";
import { Button, toast } from "../../design/index.js";
import { api, isApiError } from "../../lib/api.js";
import "./profile.css";

export type VitalsMap = Record<string, unknown>;

const SELECT_FIELDS: Record<string, string[]> = {
  drinking: ["", "never", "sometimes", "socially", "often"],
  smoking: ["", "never", "sometimes", "often", "quitting"],
  kids: ["", "have", "want", "don't want", "open"],
  zodiac: [
    "",
    "aries",
    "taurus",
    "gemini",
    "cancer",
    "leo",
    "virgo",
    "libra",
    "scorpio",
    "sagittarius",
    "capricorn",
    "aquarius",
    "pisces",
  ],
};

const TEXT_FIELDS = ["job", "education", "religion", "politics"] as const;

export interface VitalsEditorProps {
  vitals: VitalsMap;
  onSaved: () => void;
}

export function VitalsEditor({ vitals, onSaved }: VitalsEditorProps) {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const key of [...Object.keys(SELECT_FIELDS), ...TEXT_FIELDS, "height_cm"]) {
      const v = vitals[key];
      next[key] = v === undefined || v === null ? "" : String(v);
    }
    setDraft(next);
    setDirty(false);
  }, [vitals]);

  function update(field: string, value: string) {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  }

  async function onSave() {
    if (busy) return;
    const body: VitalsMap = {};
    for (const [field, value] of Object.entries(draft)) {
      if (field === "height_cm") {
        body[field] = value === "" ? null : Number(value);
      } else {
        body[field] = value === "" ? null : value;
      }
    }
    setBusy(true);
    try {
      await api.patch("/me/profile", { body: { vitals: body } });
      setDirty(false);
      toast("Vitals saved.", "success");
      onSaved();
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="vitals-editor">
      <div className="vitals-grid">
        <div className="field">
          <label htmlFor="vital-height">Height (cm)</label>
          <input
            id="vital-height"
            type="number"
            min={90}
            max={250}
            value={draft.height_cm ?? ""}
            onChange={(e) => update("height_cm", e.target.value)}
          />
        </div>
        {TEXT_FIELDS.map((field) => (
          <div className="field" key={field}>
            <label htmlFor={`vital-${field}`}>{field[0]!.toUpperCase() + field.slice(1)}</label>
            <input
              id={`vital-${field}`}
              type="text"
              maxLength={80}
              value={draft[field] ?? ""}
              onChange={(e) => update(field, e.target.value)}
            />
          </div>
        ))}
        {Object.entries(SELECT_FIELDS).map(([field, options]) => (
          <div className="field" key={field}>
            <label htmlFor={`vital-${field}`}>{field[0]!.toUpperCase() + field.slice(1)}</label>
            <select id={`vital-${field}`} value={draft[field] ?? ""} onChange={(e) => update(field, e.target.value)}>
              {options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt === "" ? "—" : opt}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
        <Button size="sm" variant="primary" loading={busy} disabled={!dirty} onClick={() => void onSave()}>
          Save vitals
        </Button>
      </div>
    </div>
  );
}
